import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Palette, Plus, Loader2, X, Printer, Download } from 'lucide-react';
import { projectAPI, settingsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

// Menu défilant de l'étage, RDC par défaut. Liste de départ reprenant le vocabulaire des choix déjà
// saisis (RDC, R+1, Tous) ; elle évolue ensuite depuis le menu lui-même (« Modifier la liste »),
// enregistrée dans les paramètres (colorFloors) et partagée par tous les chantiers. Les anciennes
// saisies libres restent affichées telles quelles dans le tableau.
const DEFAULT_FLOORS = ['RDC', 'R+1', 'R+2', 'R+3', 'R+4', 'Sous-sol', 'Combles', 'Extérieur', 'Tous'];
const NEW_FLOOR = '__nouveau__';
const EMPTY_FORM = { floor: DEFAULT_FLOORS[0], room: '', color: '', comment: '' };

const ColorChoiceDialog = ({ project, isOpen, onOpenChange }) => {
  const { toast } = useToast();
  const [choices, setChoices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [floors, setFloors] = useState(DEFAULT_FLOORS);
  const [managingFloors, setManagingFloors] = useState(false);
  const [newFloor, setNewFloor] = useState('');

  // Chargée à chaque ouverture : une liste modifiée sur un autre appareil est reprise.
  const loadFloors = useCallback(async () => {
    try {
      const settings = await settingsAPI.get();
      if (Array.isArray(settings.colorFloors) && settings.colorFloors.length) {
        setFloors(settings.colorFloors);
        setForm((f) => (settings.colorFloors.includes(f.floor) ? f : { ...f, floor: settings.colorFloors[0] }));
      }
    } catch (error) {
      // Liste par défaut : le menu reste utilisable sans les paramètres
    }
  }, []);

  const saveFloors = async (list, selected) => {
    try {
      await settingsAPI.update({ colorFloors: list });
      setFloors(list);
      setForm((f) => ({ ...f, floor: selected && list.includes(selected) ? selected : (list.includes(f.floor) ? f.floor : list[0]) }));
      return true;
    } catch (error) {
      toast({
        title: 'Liste des étages non enregistrée',
        description: error?.response?.data?.detail || String(error),
        variant: 'destructive',
        duration: 6000,
      });
      return false;
    }
  };

  const handleAddFloor = async () => {
    const name = newFloor.trim();
    if (!name) return;
    const existing = floors.find((f) => f.toLowerCase() === name.toLowerCase());
    if (existing) {
      setForm((f) => ({ ...f, floor: existing }));
      setNewFloor('');
      return;
    }
    if (await saveFloors([...floors, name], name)) setNewFloor('');
  };

  const handleRemoveFloor = async (name) => {
    if (floors.length <= 1) return;
    await saveFloors(floors.filter((f) => f !== name));
  };

  const loadChoices = useCallback(async () => {
    if (!project) return;
    setIsLoading(true);
    try {
      const result = await projectAPI.getColorChoices(project.id);
      setChoices(result.choices || []);
    } catch (error) {
      toast({
        title: 'Erreur',
        description: "Impossible de charger les choix de couleur",
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [project, toast]);

  useEffect(() => {
    if (isOpen && project) {
      setForm(EMPTY_FORM);
      setManagingFloors(false);
      setNewFloor('');
      loadChoices();
      loadFloors();
    }
    // eslint-disable-next-line
  }, [isOpen, project?.id]);

  const handleSave = async () => {
    if (!form.color.trim()) return;
    setIsSaving(true);
    try {
      await projectAPI.addColorChoice(project.id, form);
      toast({
        title: 'Choix de couleur enregistré',
        description: `${project.name} — ${form.room || 'pièce'} : ${form.color}`,
        duration: 4000,
      });
      // L'étage et la pièce restent ceux saisis : on enchaîne les pièces d'un même étage, et
      // plusieurs couleurs pour une même pièce (murs, plafond, boiseries...). Couleur et
      // commentaire, eux, repartent à zéro.
      setForm({ ...EMPTY_FORM, floor: form.floor, room: form.room });
      await loadChoices();
    } catch (error) {
      toast({
        title: "Échec de l'enregistrement",
        description: error?.response?.data?.detail || String(error),
        variant: 'destructive',
        duration: 6000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (choice) => {
    setDeletingId(choice.id);
    try {
      await projectAPI.deleteColorChoice(project.id, choice.id);
      toast({
        title: 'Choix supprimé',
        description: `${choice.room || 'Choix'} — ${choice.color}`,
        duration: 3000,
      });
      await loadChoices();
    } catch (error) {
      toast({
        title: 'Échec de la suppression',
        description: error?.response?.data?.detail || String(error),
        variant: 'destructive',
        duration: 5000,
      });
      await loadChoices();
    } finally {
      setDeletingId(null);
    }
  };

  // Imprimer : la fiche s'ouvre dans un nouvel onglet, dans le lecteur PDF du
  // navigateur, bouton Imprimer à portée de clic. Sur téléphone, ce même lecteur
  // propose Imprimer et Partager (mail, messagerie) — plus fiable que de lancer
  // l'impression d'office, qui se comporte différemment d'un navigateur à l'autre.
  const handlePrint = () => {
    window.open(projectAPI.colorChoicesPdfUrl(project.id), '_blank', 'noopener');
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const blob = await projectAPI.colorChoicesPdf(project.id);
      const url = URL.createObjectURL(blob);
      const lien = document.createElement('a');
      lien.href = url;
      // Caractères interdits dans un nom de fichier Windows : remplacés par un tiret.
      lien.download = `Choix couleurs - ${project.name}.pdf`.replace(/[\\/:*?"<>|]/g, '-');
      document.body.appendChild(lien);
      lien.click();
      lien.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      toast({
        title: 'Téléchargement impossible',
        description: error?.response?.status === 401 ? 'Session expirée, reconnectez-vous' : 'Réessayez',
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  if (!project) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-white border border-purple-100 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center text-purple-800">
            <Palette className="w-5 h-5 mr-2" />
            Choix de couleur — {project.name}
          </DialogTitle>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={handlePrint}
                    className="border-purple-200 text-purple-700 hover:bg-purple-50"
                    title="Ouvre la fiche en PDF, prête à imprimer">
              <Printer className="w-4 h-4 mr-2" /> Imprimer
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownload} disabled={isDownloading}
                    className="border-purple-200 text-purple-700 hover:bg-purple-50"
                    title="Enregistre la fiche en PDF, par exemple pour l'envoyer au client">
              {isDownloading
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <Download className="w-4 h-4 mr-2" />}
              Télécharger
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-5">
          {/* Choix déjà enregistrés */}
          <div>
            <Label className="text-purple-700">Choix déjà enregistrés</Label>
            {isLoading ? (
              <p className="text-sm text-gray-500 mt-2 flex items-center">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Chargement...
              </p>
            ) : choices.length === 0 ? (
              <p className="text-sm text-gray-500 mt-2">Aucun choix enregistré pour ce chantier.</p>
            ) : (
              <div className="mt-2 border border-purple-100 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-purple-50 text-left text-xs text-purple-700">
                      <th className="px-3 py-2 font-medium">Étage</th>
                      <th className="px-3 py-2 font-medium">Pièce</th>
                      <th className="px-3 py-2 font-medium">Couleur</th>
                      <th className="px-3 py-2 font-medium">Commentaire</th>
                      <th className="px-2 py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {choices.map((c) => (
                      <tr key={c.id} className="group border-t border-purple-50 hover:bg-purple-50/50">
                        <td className="px-3 py-2 text-gray-700">{c.floor || '—'}</td>
                        <td className="px-3 py-2 text-gray-700">{c.room || '—'}</td>
                        <td className="px-3 py-2 text-gray-900 font-medium">{c.color}</td>
                        <td className="px-3 py-2 text-gray-500">{c.comment || '—'}</td>
                        <td className="px-2 py-2">
                          <button
                            onClick={() => handleDelete(c)}
                            disabled={deletingId === c.id}
                            className="hidden group-hover:flex items-center justify-center text-red-500 hover:text-red-700 disabled:opacity-50"
                            title="Supprimer ce choix"
                          >
                            {deletingId === c.id
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <X className="w-3.5 h-3.5" />}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Ajouter un choix */}
          <div className="bg-purple-50 p-4 rounded-lg space-y-3">
            <h3 className="text-sm font-medium text-purple-800">Ajouter un choix</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="cc-floor" className="text-purple-700 text-xs">Étage</Label>
                <select id="cc-floor" value={form.floor}
                        onChange={(e) => (e.target.value === NEW_FLOOR
                          ? setManagingFloors(true)
                          : setForm((f) => ({ ...f, floor: e.target.value })))}
                        className="w-full h-9 px-3 bg-white border border-purple-200 rounded-md text-sm focus:ring-2 focus:ring-purple-200 focus:border-purple-400">
                  {floors.map((floor) => <option key={floor} value={floor}>{floor}</option>)}
                  <option value={NEW_FLOOR}>＋ Nouvel étage…</option>
                </select>
                <button type="button" onClick={() => setManagingFloors((v) => !v)}
                        className="text-xs text-purple-600 hover:underline">
                  {managingFloors ? 'Fermer la liste' : 'Modifier la liste'}
                </button>
              </div>
              <div className="space-y-1">
                <Label htmlFor="cc-room" className="text-purple-700 text-xs">Pièce</Label>
                <Input id="cc-room" value={form.room} placeholder="Salon, chambre 1..."
                       onChange={(e) => setForm((f) => ({ ...f, room: e.target.value }))}
                       className="border-purple-200" />
              </div>
            </div>
            {managingFloors && (
              <div className="bg-white border border-purple-200 rounded-md p-3 space-y-2">
                <div className="text-xs font-medium text-purple-800">Étages proposés dans le menu</div>
                <div className="flex flex-wrap gap-1.5">
                  {floors.map((floor) => (
                    <span key={floor} className="inline-flex items-center gap-1 bg-purple-50 border border-purple-200 rounded-full pl-2.5 pr-1 py-0.5 text-xs text-purple-900">
                      {floor}
                      {floors.length > 1 && (
                        <button type="button" onClick={() => handleRemoveFloor(floor)}
                                title={`Retirer « ${floor} » du menu`}
                                className="text-purple-400 hover:text-red-600">
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={newFloor} placeholder="Nouvel étage (ex : R+5, Garage…)" autoFocus
                         onChange={(e) => setNewFloor(e.target.value)}
                         onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddFloor(); } }}
                         className="border-purple-200 h-8" />
                  <Button type="button" size="sm" onClick={handleAddFloor} disabled={!newFloor.trim()}
                          className="bg-purple-600 hover:bg-purple-700 text-white h-8">
                    Ajouter
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  La liste est commune à tous les chantiers. En retirer un étage ne modifie pas les choix déjà enregistrés.
                </p>
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="cc-color" className="text-purple-700 text-xs">Couleur</Label>
              <Input id="cc-color" value={form.color} placeholder="Blanc cassé RAL 9010..."
                     onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                     className="border-purple-200" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cc-comment" className="text-purple-700 text-xs">Commentaire (optionnel)</Label>
              <Textarea id="cc-comment" value={form.comment} rows={2}
                        onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
                        className="border-purple-200" />
            </div>
            <Button
              onClick={handleSave}
              disabled={!form.color.trim() || isSaving}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              {isSaving ? 'Enregistrement...' : 'Ajouter ce choix'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ColorChoiceDialog;
